/**
 * BPR knowledge-graph contracts (the node/edge IR)
 * ------------------------------------------------
 * Zod schemas + the relation-rule validator for the BPR knowledge-graph
 * pipeline: raw discovery transcripts -> typed nodes + edges (the "inverted
 * triangle" intermediate representation) -> deterministic prioritization.
 *
 * The graph is LLM-produced JSON cached in sales.bpr_graph_version.graph_json
 * - NOT a graph database. Field names inside the IR are snake_case because the
 * IR shape is the LLM-facing document format pinned by the pipeline spec
 * (bpr-module-build-plan.md, Knowledge-Graph Pipeline sections 3-4); the
 * surrounding API DTOs follow the usual camelCase conventions.
 *
 * Layering:
 * - The node/edge Zod schemas stay structurally permissive so the AI runtime
 *   can feed validation issues back to the model for self-correction.
 * - validateBprGraph() enforces the CROSS-NODE relation rules (the section-4
 *   matrix) so the model cannot emit a nonsensical graph; the strict schema
 *   (bprGraphExtractionSchema) wires it into generateValidated's retry loop.
 *
 * Guardrails encoded here (spec section 7):
 * - Extraction emits findings + open QUESTIONS only; solutions are forbidden
 *   in extraction mode (any fix idea lives in finding.meta.hypothesis).
 * - Every Finding and Question carries >=1 verbatim evidence span.
 * - Client signal is edge-driven: meta.client_signal 'escalated' /
 *   'deprioritized' MUST be matched by an ESCALATED / DEPRIORITIZED edge
 *   (the deterministic prioritizer reads only edges).
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Value vocabularies
// ---------------------------------------------------------------------------
export const BPR_GRAPH_NODE_TYPES = [
    'System',
    'Functional',
    'Data',
    'Finding',
    'Question',
    'Solution',
];
export const bprGraphNodeTypeSchema = z.enum(BPR_GRAPH_NODE_TYPES);
export const BPR_GRAPH_RELS = [
    'MANIFESTS_IN',
    'ROOT_CAUSE_OF',
    'DEPENDS_ON',
    'BLOCKS',
    'EVIDENCED_BY',
    'ADDRESSED_BY',
    'RAISED_BY',
    'ESCALATED',
    'DEPRIORITIZED',
];
export const bprGraphRelSchema = z.enum(BPR_GRAPH_RELS);
export const BPR_GRAPH_WORK_TYPES = [
    'Configuration',
    'Integration',
    'Data migration',
    'Implementation',
    'Assessment',
    'Advisory',
    'Troubleshooting',
];
export const bprGraphWorkTypeSchema = z.enum(BPR_GRAPH_WORK_TYPES);
export const BPR_GRAPH_CLIENT_SIGNALS = [
    'escalated',
    'requested',
    'neutral',
    'deprioritized',
];
export const bprGraphClientSignalSchema = z.enum(BPR_GRAPH_CLIENT_SIGNALS);
export const BPR_GRAPH_EFFORTS = ['S', 'M', 'L', 'XL'];
export const bprGraphEffortSchema = z.enum(BPR_GRAPH_EFFORTS);
/** Priority tiers, apex -> base of the inverted triangle. */
export const BPR_GRAPH_PRIORITY_TIERS = ['Critical', 'High', 'Medium', 'Low'];
export const bprGraphPriorityTierSchema = z.enum(BPR_GRAPH_PRIORITY_TIERS);
/** Canonical triangle layer per node type (1 widest -> 3 narrowest; null off-triangle). */
export const BPR_GRAPH_LAYER_BY_TYPE = {
    System: 1,
    Functional: 2,
    Data: 3,
    Finding: null,
    Question: null,
    Solution: null,
};
// ---------------------------------------------------------------------------
// Node / edge shapes
// ---------------------------------------------------------------------------
/** A verbatim transcript span grounding a node (feeds the audit history). */
export const bprGraphEvidenceSpanSchema = z.object({
    /** Verbatim quote from the transcript. */
    quote: z.string().min(1),
    speaker: z.string().nullable().optional(),
    /** e.g. '15:20:19' when the transcript carries timestamps. */
    timestamp: z.string().nullable().optional(),
    /** knowledge.document uuid (or eval fixture id) of the source transcript. */
    source_doc_id: z.string().min(1),
});
/** Finding-specific meta (the scorer's inputs live here). */
export const bprGraphFindingMetaSchema = z.object({
    work_type: bprGraphWorkTypeSchema,
    problem: z.string().min(1),
    /** Candidate solution as HYPOTHESIS, never committed scope. */
    hypothesis: z.string().nullable().optional(),
    client_signal: bprGraphClientSignalSchema.default('neutral'),
    /** Touches rev rec, tax, intercompany, close. */
    financial_audit_risk: z.boolean().default(false),
    blocks_go_live: z.boolean().default(false),
    /** false = still an open decision gating design. */
    decided: z.boolean().default(true),
    /** Optional sizing; sequences items WITHIN a tier, never ranks importance. */
    effort: bprGraphEffortSchema.optional(),
});
export const bprGraphQuestionMetaSchema = z.object({
    /** Does downstream design depend on the answer. */
    blocking: z.boolean().default(false),
    /** e.g. 'Vivek', 'Palmetto team'. */
    owner: z.string().nullable().optional(),
    /** e.g. 'bring to session 2'. */
    due_context: z.string().nullable().optional(),
});
export const bprGraphSolutionMetaSchema = z.object({
    /** Link to an authoritative source pattern, when matched. */
    pattern_id: z.string().nullable().optional(),
    /** Where synthesized from. */
    source: z.string().optional(),
    effort: bprGraphEffortSchema.optional(),
    /** Prefer native platform capability before add-ons. */
    native_first: z.boolean().optional(),
});
const bprGraphNodeBase = {
    /** Stable slug id, e.g. 'data.finco_id' (convention: '<type>.<slug>'). */
    id: z.string().min(1),
    /** Short human label. */
    label: z.string().min(1),
    /** 1-2 sentences. */
    summary: z.string().optional(),
    /** Model calibration 0..1 - must actually vary (guardrail 3). */
    confidence: z.number().min(0).max(1).optional(),
    /** Triangle layer; canonical value derives from type (normalize fills it). */
    layer: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable().optional(),
    evidence: z.array(bprGraphEvidenceSpanSchema).optional(),
};
/** Loose per-type meta for the triangle layers (systems/areas/data entities). */
const bprGraphLooseMetaSchema = z.record(z.unknown()).optional();
export const bprGraphNodeSchema = z.discriminatedUnion('type', [
    z.object({ ...bprGraphNodeBase, type: z.literal('System'), meta: bprGraphLooseMetaSchema }),
    z.object({ ...bprGraphNodeBase, type: z.literal('Functional'), meta: bprGraphLooseMetaSchema }),
    z.object({ ...bprGraphNodeBase, type: z.literal('Data'), meta: bprGraphLooseMetaSchema }),
    z.object({
        ...bprGraphNodeBase,
        type: z.literal('Finding'),
        confidence: z.number().min(0).max(1),
        evidence: z.array(bprGraphEvidenceSpanSchema).min(1),
        meta: bprGraphFindingMetaSchema,
    }),
    z.object({
        ...bprGraphNodeBase,
        type: z.literal('Question'),
        confidence: z.number().min(0).max(1),
        evidence: z.array(bprGraphEvidenceSpanSchema).min(1),
        meta: bprGraphQuestionMetaSchema.default({}),
    }),
    z.object({
        ...bprGraphNodeBase,
        type: z.literal('Solution'),
        meta: bprGraphSolutionMetaSchema.default({}),
    }),
]);
export const bprGraphEdgeSchema = z.object({
    from: z.string().min(1),
    to: z.string().min(1),
    rel: bprGraphRelSchema,
    note: z.string().nullable().optional(),
});
/** The full graph IR stored in sales.bpr_graph_version.graph_json. */
export const bprGraphContentSchema = z.object({
    nodes: z.array(bprGraphNodeSchema),
    edges: z.array(bprGraphEdgeSchema),
});
/**
 * Allowed (from-type, to-type) pairs per relation. 'null' endpoint entries
 * mean the endpoint may be UNRESOLVED (not a node in the graph):
 * - EVIDENCED_BY 'to' points at a transcript span/document, not a node.
 * - RAISED_BY / ESCALATED / DEPRIORITIZED 'from' is the client (an actor,
 *   not one of the six node types).
 * Everything else must resolve to a node of an allowed type.
 */
const EDGE_RULES = {
    MANIFESTS_IN: { from: ['Finding', 'Functional'], to: ['Functional', 'System'] },
    ROOT_CAUSE_OF: { from: ['Data'], to: ['Finding'] },
    DEPENDS_ON: { from: ['Finding'], to: ['Finding'] },
    BLOCKS: { from: ['Finding'], to: [...BPR_GRAPH_NODE_TYPES] },
    EVIDENCED_BY: { from: ['Finding', 'Question'], to: null },
    ADDRESSED_BY: { from: ['Finding'], to: ['Solution'] },
    RAISED_BY: { from: null, to: ['Finding'] },
    ESCALATED: { from: null, to: ['Finding'] },
    DEPRIORITIZED: { from: null, to: ['Finding'] },
};
/** MANIFESTS_IN is the one rel whose from/to pairs are coupled, not a cross product. */
const MANIFESTS_IN_PAIRS = [
    ['Finding', 'Functional'],
    ['Functional', 'System'],
];
const NODE_ID_PREFIX_BY_TYPE = {
    System: 'system.',
    Functional: 'functional.',
    Data: 'data.',
    Finding: 'finding.',
    Question: 'question.',
    Solution: 'solution.',
};
function edgeKey(edge) {
    return `${edge.from}|${edge.rel}|${edge.to}`;
}
/**
 * Enforce the relation rules over a structurally-valid graph. Errors fail
 * validation (and feed the model self-correction loop); warnings surface in
 * reports but never block.
 */
export function validateBprGraph(graph, options = {}) {
    const errors = [];
    const warnings = [];
    const nodeById = new Map();
    for (const node of graph.nodes) {
        if (nodeById.has(node.id)) {
            errors.push({ rule: 'duplicate_node_id', message: `duplicate node id: ${node.id}`, at: node.id });
            continue;
        }
        nodeById.set(node.id, node);
        const canonicalLayer = BPR_GRAPH_LAYER_BY_TYPE[node.type];
        if (node.layer != null && node.layer !== canonicalLayer) {
            errors.push({
                rule: 'layer_type_mismatch',
                message: `node ${node.id} (${node.type}) declares layer ${node.layer}; expected ${canonicalLayer ?? 'null'}`,
                at: node.id,
            });
        }
        const prefix = NODE_ID_PREFIX_BY_TYPE[node.type];
        if (!node.id.startsWith(prefix)) {
            warnings.push({
                rule: 'id_prefix_convention',
                message: `node ${node.id} (${node.type}) does not follow the '${prefix}<slug>' id convention`,
                at: node.id,
            });
        }
        if (options.forbidSolutions && node.type === 'Solution') {
            errors.push({
                rule: 'solution_in_extraction',
                message: `Solution node ${node.id} is not allowed during extraction - no solutioning; ` +
                    'put candidate fixes in the finding meta.hypothesis instead',
                at: node.id,
            });
        }
    }
    const seenEdges = new Set();
    for (const edge of graph.edges) {
        const at = edgeKey(edge);
        if (seenEdges.has(at)) {
            // Duplicates would double-count in the centrality scorer.
            errors.push({ rule: 'duplicate_edge', message: `duplicate edge: ${at}`, at });
            continue;
        }
        seenEdges.add(at);
        if (edge.from === edge.to) {
            errors.push({ rule: 'self_edge', message: `self-referencing edge: ${at}`, at });
            continue;
        }
        if (options.forbidSolutions && edge.rel === 'ADDRESSED_BY') {
            errors.push({
                rule: 'solution_in_extraction',
                message: `ADDRESSED_BY edge is not allowed during extraction: ${at}`,
                at,
            });
            continue;
        }
        const rule = EDGE_RULES[edge.rel];
        const fromNode = nodeById.get(edge.from);
        const toNode = nodeById.get(edge.to);
        if (rule.from !== null) {
            if (!fromNode) {
                errors.push({
                    rule: 'edge_endpoint_unresolved',
                    message: `edge ${at}: 'from' does not resolve to a node`,
                    at,
                });
            }
            else if (!rule.from.includes(fromNode.type)) {
                errors.push({
                    rule: 'edge_type_mismatch',
                    message: `edge ${at}: ${edge.rel} cannot originate from a ${fromNode.type} node`,
                    at,
                });
            }
        }
        if (rule.to !== null) {
            if (!toNode) {
                errors.push({
                    rule: 'edge_endpoint_unresolved',
                    message: `edge ${at}: 'to' does not resolve to a node`,
                    at,
                });
            }
            else if (!rule.to.includes(toNode.type)) {
                errors.push({
                    rule: 'edge_type_mismatch',
                    message: `edge ${at}: ${edge.rel} cannot point at a ${toNode.type} node`,
                    at,
                });
            }
        }
        // MANIFESTS_IN pairs are coupled: Finding->Functional or Functional->System.
        if (edge.rel === 'MANIFESTS_IN' && fromNode && toNode) {
            const ok = MANIFESTS_IN_PAIRS.some(([f, t]) => fromNode.type === f && toNode.type === t);
            if (!ok) {
                errors.push({
                    rule: 'edge_type_mismatch',
                    message: `edge ${at}: MANIFESTS_IN must be Finding->Functional or Functional->System, ` +
                        `got ${fromNode.type}->${toNode.type}`,
                    at,
                });
            }
        }
    }
    // Client-signal consistency: the prioritizer reads EDGES, so a node claiming
    // escalated/deprioritized without the matching edge would silently lose its
    // hard override - force the model to add the edge.
    const escalatedTo = new Set(graph.edges.filter((e) => e.rel === 'ESCALATED').map((e) => e.to));
    const deprioritizedTo = new Set(graph.edges.filter((e) => e.rel === 'DEPRIORITIZED').map((e) => e.to));
    for (const node of graph.nodes) {
        if (node.type !== 'Finding')
            continue;
        const signal = node.meta.client_signal;
        if (signal === 'escalated' && !escalatedTo.has(node.id)) {
            errors.push({
                rule: 'client_signal_edge_missing',
                message: `finding ${node.id} has client_signal 'escalated' but no ESCALATED edge points at it`,
                at: node.id,
            });
        }
        if (signal === 'deprioritized' && !deprioritizedTo.has(node.id)) {
            errors.push({
                rule: 'client_signal_edge_missing',
                message: `finding ${node.id} has client_signal 'deprioritized' but no DEPRIORITIZED edge points at it`,
                at: node.id,
            });
        }
        if (escalatedTo.has(node.id) && signal !== 'escalated') {
            warnings.push({
                rule: 'client_signal_edge_mismatch',
                message: `finding ${node.id} has an ESCALATED edge but client_signal '${signal}' (edge wins)`,
                at: node.id,
            });
        }
        if (deprioritizedTo.has(node.id) && signal !== 'deprioritized') {
            warnings.push({
                rule: 'client_signal_edge_mismatch',
                message: `finding ${node.id} has a DEPRIORITIZED edge but client_signal '${signal}' (edge wins)`,
                at: node.id,
            });
        }
    }
    return { errors, warnings };
}
/** Fill canonical layers by type; leaves everything else untouched. */
export function normalizeBprGraph(graph) {
    return {
        ...graph,
        nodes: graph.nodes.map((node) => ({ ...node, layer: BPR_GRAPH_LAYER_BY_TYPE[node.type] })),
    };
}
/**
 * The strict schema the graph-build skill validates model output against:
 * structural shape + the relation rules + the extraction guardrail, so
 * generateValidated's retry loop feeds relation violations back to the model.
 */
export const bprGraphExtractionSchema = bprGraphContentSchema.superRefine((graph, ctx) => {
    const { errors } = validateBprGraph(graph, { forbidSolutions: true });
    for (const issue of errors) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `[${issue.rule}] ${issue.message}`,
            path: issue.at ? ['(graph)', issue.at] : ['(graph)'],
        });
    }
});
// ---------------------------------------------------------------------------
// Prioritization output (Stage C wire shapes; computed by deterministic app
// code in apps/api, never by an LLM call)
// ---------------------------------------------------------------------------
export const bprGraphPriorityItemSchema = z.object({
    findingId: z.string(),
    label: z.string(),
    tier: bprGraphPriorityTierSchema,
    /** Weighted score (0 when a hard override decided the tier). */
    score: z.number(),
    /** Count of DEPENDS_ON / ROOT_CAUSE_OF edges whose target reaches this finding. */
    downstream: z.number().int(),
    /** The distinct nodes that (transitively) depend on this finding (score explainer). */
    dependentIds: z.array(z.string()),
    /** Explicit client intent that bypassed the weighted score. */
    hardOverride: z.enum(['escalated', 'deprioritized']).nullable(),
    factors: z.object({
        blocksGoLive: z.boolean(),
        financialAuditRisk: z.boolean(),
        undecided: z.boolean(),
    }),
    /** Sequencing hint within the tier; never ranks importance. */
    effort: bprGraphEffortSchema.nullable(),
    /** Data-layer nodes that are ROOT_CAUSE_OF this finding ("root cause rises"). */
    rootCauseIds: z.array(z.string()),
});
export const bprGraphPriorityResultSchema = z.object({
    tiers: z.array(bprGraphPriorityTierSchema),
    /** Ranked apex -> base; within a tier: overrides first, then score desc, effort asc, id. */
    items: z.array(bprGraphPriorityItemSchema),
});
/** Per-tier finding rollup (shared by the system view and the area rollup). */
export const bprGraphFindingCountsSchema = z.object({
    Critical: z.number().int(),
    High: z.number().int(),
    Medium: z.number().int(),
    Low: z.number().int(),
    total: z.number().int(),
});
/**
 * Per-functional-area rollup of the finding priorities (findings join their
 * areas via MANIFESTS_IN). Drives section-level ordering surfaces (e.g. the
 * deal Prioritization tab groups) - decreasing priority order.
 */
export const bprGraphAreaPrioritySchema = z.object({
    /** Functional node id, e.g. 'functional.order_to_cash'. */
    areaId: z.string(),
    label: z.string(),
    /** Highest tier among the area's findings. */
    topTier: bprGraphPriorityTierSchema,
    /** Sum of the area's finding scores (hard-overridden findings score 0). */
    score: z.number(),
    findingCounts: bprGraphFindingCountsSchema,
    findingIds: z.array(z.string()),
});
export const bprGraphAreaPriorityResultSchema = z.object({
    /** Ordered decreasing: best tier first, then count at that tier, then score. */
    areas: z.array(bprGraphAreaPrioritySchema),
});
// ---------------------------------------------------------------------------
// Diagram model (Stage D1 wire shapes; spec section 6) - render-agnostic JSON
// the front end draws, DERIVED deterministically from the cached graph in
// apps/api (graph-diagram.ts). Snake_case keys, matching the spec's shape.
// ---------------------------------------------------------------------------
/**
 * Free-form System-node meta convention for integration points (spec section
 * 2 treats integrations as layer-1 nodes, e.g. an "SF-NS sync"): the graph
 * build stamps { kind: 'integration', connects: [system ids], status }.
 * Parsed defensively - a declared integration keeps its kind, but malformed
 * connects/status simply yield no edges and no gap flag (nothing throws).
 */
export const BPR_GRAPH_INTEGRATION_STATUSES = ['working', 'gap', 'planned'];
export const bprGraphIntegrationStatusSchema = z.enum(BPR_GRAPH_INTEGRATION_STATUSES);
export const bprGraphSystemViewNodeSchema = z.object({
    /** System node id from the graph IR. */
    id: z.string(),
    label: z.string(),
    summary: z.string().optional(),
    /** 'integration' when the node represents a sync/integration point. */
    kind: z.enum(['system', 'integration']),
    confidence: z.number().nullable(),
    /** Findings located on this system via MANIFESTS_IN chains / BLOCKS edges. */
    finding_counts: bprGraphFindingCountsSchema,
    /** Integration gap flag (failing/missing sync, or an integration carrying findings). */
    gap: z.boolean(),
});
export const bprGraphSystemViewEdgeSchema = z.object({
    /** System node ids (unordered pair semantically; from/to for drawing). */
    from: z.string(),
    to: z.string(),
    /** Integration-point node id when the edge comes from one; null for a
     *  co-manifestation seam (a process domain hosted on both systems). */
    via: z.string().nullable(),
    label: z.string(),
    gap: z.boolean(),
    finding_ids: z.array(z.string()),
});
/** One weighted signal that fed a finding's priority score (score explainer). */
export const bprGraphScoreComponentSchema = z.object({
    /** Stable signal key ('centrality' | 'blocks_go_live' | ...). */
    key: z.string(),
    label: z.string(),
    /** Optional human detail (e.g. the centrality dependent count x weight). */
    detail: z.string().nullable(),
    /** Points this signal contributed to the score (0 when it did not apply). */
    points: z.number(),
    /** Whether the signal fired for this finding. */
    active: z.boolean(),
    /** For centrality: the items that (transitively) depend on this finding. */
    dependents: z.array(z.object({ id: z.string(), label: z.string() })).nullable(),
});
export const bprGraphPriorityViewItemSchema = z.object({
    finding_id: z.string(),
    tier: bprGraphPriorityTierSchema,
    root_cause_ids: z.array(z.string()),
    // Additive display fields beyond the minimal spec shape.
    label: z.string(),
    score: z.number(),
    hard_override: z.enum(['escalated', 'deprioritized']).nullable(),
    effort: bprGraphEffortSchema.nullable(),
    root_causes: z.array(z.object({ id: z.string(), label: z.string() })),
    /** How the weighted score was built (null when a hard override decided the tier). */
    score_breakdown: z
        .object({
        components: z.array(bprGraphScoreComponentSchema),
        total: z.number(),
        /** Minimum score for the tier this item landed in. */
        tier_min: z.number(),
    })
        .nullable(),
});
export const bprGraphDiagramSchema = z.object({
    system_view: z.object({
        nodes: z.array(bprGraphSystemViewNodeSchema),
        edges: z.array(bprGraphSystemViewEdgeSchema),
    }),
    priority_view: z.object({
        tiers: z.array(bprGraphPriorityTierSchema),
        items: z.array(bprGraphPriorityViewItemSchema),
    }),
});
// ---------------------------------------------------------------------------
// Entity DTOs + request contracts (API wire shapes; uuid-only)
// ---------------------------------------------------------------------------
export const bprGraphVersionSummarySchema = z.object({
    uuid: z.string().uuid(),
    engagementUuid: z.string().uuid().nullable().optional(),
    /** knowledge.document uuids (or eval fixture ids) that fed the build. */
    sourceDocIds: z.array(z.string()),
    modelRef: z.string().nullable().optional(),
    promptRef: z.string().nullable().optional(),
    promptHash: z.string().nullable().optional(),
    generatedAt: z.string().nullable().optional(),
    createdAt: z.string(),
    stats: z
        .object({
        nodes: z.number().int(),
        edges: z.number().int(),
        findings: z.number().int(),
        questions: z.number().int(),
    })
        .optional(),
});
export const bprGraphVersionDetailSchema = bprGraphVersionSummarySchema.extend({
    graph: bprGraphContentSchema,
});
export const bprBuildGraphRequestSchema = z.object({
    /** Rebuild even when the latest cached version covers the same source docs. */
    force: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Overrides + feedback (spec section 9) - consultant patches on the cached
// graph, keyed by ENGAGEMENT + stable node/edge id so they re-apply on every
// regenerated version. The snapshot itself is never mutated.
// ---------------------------------------------------------------------------
export const BPR_GRAPH_OVERRIDE_OPERATIONS = ['set_field', 'suppress'];
export const bprGraphOverrideOperationSchema = z.enum(BPR_GRAPH_OVERRIDE_OPERATIONS);
export const BPR_GRAPH_OVERRIDE_TARGET_KINDS = ['node', 'edge'];
export const bprGraphOverrideTargetKindSchema = z.enum(BPR_GRAPH_OVERRIDE_TARGET_KINDS);
export const BPR_GRAPH_OVERRIDE_STATUSES = ['active', 'retired'];
export const BPR_GRAPH_FEEDBACK_ACTIONS = ['approve', 'reject', 'rate', 'comment'];
export const bprGraphFeedbackActionSchema = z.enum(BPR_GRAPH_FEEDBACK_ACTIONS);
/** Stable key addressing one edge in override target ids: '<from>|<rel>|<to>'. */
export function bprGraphEdgeKey(edge) {
    return `${edge.from}|${edge.rel}|${edge.to}`;
}
/**
 * Fields set_field may touch. 'id'/'type' are structural (they key overrides
 * and relation rules) and are never overridable; meta paths are one level
 * deep ('meta.<key>').
 */
const OVERRIDABLE_TOP_FIELDS = new Set(['label', 'summary', 'confidence']);
export const bprGraphOverrideSchema = z.object({
    uuid: z.string().uuid(),
    targetKind: bprGraphOverrideTargetKindSchema,
    targetId: z.string().min(1),
    operation: bprGraphOverrideOperationSchema,
    field: z.string().nullable().optional(),
    value: z.unknown().nullable().optional(),
    previousValue: z.unknown().nullable().optional(),
    status: z.enum(BPR_GRAPH_OVERRIDE_STATUSES),
    authoredGraphVersionUuid: z.string().uuid().nullable().optional(),
    createdAt: z.string(),
});
export const bprCreateGraphOverrideRequestSchema = z
    .object({
    targetKind: bprGraphOverrideTargetKindSchema,
    targetId: z.string().min(1),
    operation: bprGraphOverrideOperationSchema,
    /** set_field only: 'label' | 'summary' | 'confidence' | 'meta.<key>'. */
    field: z.string().optional(),
    value: z.unknown().optional(),
    previousValue: z.unknown().optional(),
})
    .superRefine((req, ctx) => {
    if (req.operation === 'set_field') {
        const field = req.field ?? '';
        const valid = OVERRIDABLE_TOP_FIELDS.has(field) || /^meta\.[a-zA-Z0-9_]+$/.test(field);
        if (!valid) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['field'],
                message: "set_field needs field 'label' | 'summary' | 'confidence' | 'meta.<key>'",
            });
        }
        if (req.value === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['value'],
                message: 'set_field needs a value',
            });
        }
        if (req.targetKind === 'edge') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['targetKind'],
                message: 'set_field only applies to nodes',
            });
        }
    }
});
export const bprGraphFeedbackRequestSchema = z.object({
    /** Node the feedback targets; absent = the whole version. */
    nodeId: z.string().optional(),
    action: bprGraphFeedbackActionSchema,
    rating: z.number().min(1).max(5).optional(),
    comment: z.string().optional(),
});
/**
 * Apply override patches to a graph, returning a NEW graph (input untouched).
 * Unknown targets are skipped - an override authored against an older
 * version must never break resolution. suppress on a node also removes every
 * edge touching it. set_field never rewrites 'id'/'type' (schema-enforced,
 * re-guarded here).
 */
export function applyBprGraphOverrides(graph, overrides) {
    const doc = JSON.parse(JSON.stringify(graph));
    for (const override of overrides) {
        if (override.operation === 'suppress') {
            if (override.targetKind === 'node') {
                const id = override.targetId;
                if (!doc.nodes.some((n) => n.id === id))
                    continue;
                doc.nodes = doc.nodes.filter((n) => n.id !== id);
                doc.edges = doc.edges.filter((e) => e.from !== id && e.to !== id);
            }
            else {
                doc.edges = doc.edges.filter((e) => bprGraphEdgeKey(e) !== override.targetId);
            }
            continue;
        }
        // set_field (nodes only)
        if (override.targetKind !== 'node')
            continue;
        const node = doc.nodes.find((n) => n.id === override.targetId);
        if (!node)
            continue;
        const field = override.field ?? '';
        if (OVERRIDABLE_TOP_FIELDS.has(field)) {
            node[field] = override.value;
        }
        else if (/^meta\.[a-zA-Z0-9_]+$/.test(field)) {
            const key = field.slice('meta.'.length);
            const meta = (node.meta ??= {});
            meta[key] = override.value;
        }
    }
    return doc;
}
// ---------------------------------------------------------------------------
// Solution options (spec D2) - LLM-proposed SolutionNodes for Critical/High
// findings, matched against the authoritative framework's patterns. Proposals
// merge into a NEW graph version; approval is an override
// (set_field meta.approved=true) + an 'approve' feedback row.
// ---------------------------------------------------------------------------
/** One proposed option: the Solution node plus the finding it addresses. */
export const bprGraphSolutionProposalSchema = z.object({
    finding_id: z.string().min(1),
    solution: z.object({
        id: z.string().min(1),
        label: z.string().min(1),
        summary: z.string(),
        confidence: z.number().min(0).max(1).optional(),
        meta: bprGraphSolutionMetaSchema.default({}),
    }),
});
export const bprGraphSolutionsResultSchema = z.object({
    solutions: z.array(bprGraphSolutionProposalSchema),
});
/**
 * Merge proposed solutions into a graph as Solution nodes + ADDRESSED_BY
 * edges, returning a NEW graph. Skips proposals whose finding does not exist;
 * dedupes nodes by id and edges by key; normalizes solution ids onto the
 * 'solution.' prefix convention.
 */
export function mergeBprGraphSolutions(graph, proposals) {
    const doc = JSON.parse(JSON.stringify(graph));
    const nodeIds = new Set(doc.nodes.map((n) => n.id));
    const findingIds = new Set(doc.nodes.filter((n) => n.type === 'Finding').map((n) => n.id));
    const edgeKeys = new Set(doc.edges.map((e) => bprGraphEdgeKey(e)));
    const skipped = [];
    let merged = 0;
    for (const proposal of proposals) {
        if (!findingIds.has(proposal.finding_id)) {
            skipped.push(`${proposal.solution.id}: unknown finding ${proposal.finding_id}`);
            continue;
        }
        const id = proposal.solution.id.startsWith('solution.')
            ? proposal.solution.id
            : `solution.${proposal.solution.id}`;
        if (!nodeIds.has(id)) {
            doc.nodes.push({
                id,
                type: 'Solution',
                label: proposal.solution.label,
                summary: proposal.solution.summary,
                ...(proposal.solution.confidence != null
                    ? { confidence: proposal.solution.confidence }
                    : {}),
                layer: null,
                meta: proposal.solution.meta,
            });
            nodeIds.add(id);
        }
        const edge = { from: proposal.finding_id, to: id, rel: 'ADDRESSED_BY' };
        if (!edgeKeys.has(bprGraphEdgeKey(edge))) {
            doc.edges.push(edge);
            edgeKeys.add(bprGraphEdgeKey(edge));
            merged += 1;
        }
    }
    return { graph: doc, merged, skipped };
}
// ---------------------------------------------------------------------------
// Dual-audience narration (spec D2-narration / section 8.2)
// ---------------------------------------------------------------------------
export const BPR_GRAPH_NARRATION_AUDIENCES = ['business', 'technofunctional'];
export const bprGraphNarrationAudienceSchema = z.enum(BPR_GRAPH_NARRATION_AUDIENCES);
export const bprGraphNarrationResultSchema = z.object({
    audience: bprGraphNarrationAudienceSchema,
    items: z.array(z.object({
        finding_id: z.string().min(1),
        explanation: z.string().min(1),
    })),
});
export const bprGraphNarrationRequestSchema = z.object({
    audience: bprGraphNarrationAudienceSchema,
    /** Findings to narrate; absent = every Critical/High finding. */
    findingIds: z.array(z.string()).optional(),
});
/** Collect a finding's narration neighborhood; null when the finding is absent. */
export function buildBprNarrationSubgraph(graph, findingId) {
    const byId = new Map(graph.nodes.map((n) => [n.id, n]));
    const finding = byId.get(findingId);
    if (!finding || finding.type !== 'Finding')
        return null;
    const rootCauses = [];
    const manifestsIn = [];
    const dependsOn = [];
    const solutions = [];
    const seen = new Set();
    const push = (list, id) => {
        const node = byId.get(id);
        if (node && !seen.has(`${list === manifestsIn ? 'm' : ''}${id}`)) {
            list.push(node);
            seen.add(`${list === manifestsIn ? 'm' : ''}${id}`);
        }
    };
    for (const edge of graph.edges) {
        if (edge.rel === 'ROOT_CAUSE_OF' && edge.to === findingId)
            push(rootCauses, edge.from);
        if (edge.rel === 'DEPENDS_ON' && edge.from === findingId)
            push(dependsOn, edge.to);
        if (edge.rel === 'DEPENDS_ON' && edge.to === findingId)
            push(dependsOn, edge.from);
        if (edge.rel === 'ADDRESSED_BY' && edge.from === findingId)
            push(solutions, edge.to);
        if (edge.rel === 'MANIFESTS_IN' && edge.from === findingId)
            push(manifestsIn, edge.to);
    }
    // Second hop of the MANIFESTS_IN chain: Functional -> System.
    for (const area of [...manifestsIn]) {
        for (const edge of graph.edges) {
            if (edge.rel === 'MANIFESTS_IN' && edge.from === area.id)
                push(manifestsIn, edge.to);
        }
    }
    return { finding, rootCauses, manifestsIn, dependsOn, solutions };
}
//# sourceMappingURL=bpr-graph.js.map